import UserSkeleton from "./UserSkeleton";
import ProfileItem from "./ProfileItem";
import SidebarLink from "./SidebarLink";
import DownloadAppLink from "./DownloadAppLink";
import LogoutButton from "./LogoutButton";
import dynamic from "next/dynamic";
import { Fragment, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "noval";
import useTranslation from "next-translate/useTranslation";
import { Icon } from "@utils/helper";
import { buildClass, checkClass } from "@utils/tag";
import { getScreenSize } from "@utils/responsive";
import NavBar from "../NavBar";
import Link from "next/link";

const Drawer = dynamic(
  () => import("@mui/material").then((mod) => mod.Drawer),
  {
    ssr: false,
  }
);

const Slide = () => {
  const { t, lang } = useTranslation();
  const { dispatch } = useDispatch();
  const user = useSelector("user");
  const complete_kyc = user?.is_kyc_completed;
  const open = useSelector("openDrawer", false);
  const screenSize = useSelector("screenSize");
  const largeScreen = ["xs", "sm", "md", "lg"].includes(screenSize);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => {
        dispatch({ screenSize: getScreenSize(window.innerWidth) });
      };
      handleResize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  const links = useMemo(
    () => [
      {
        title: t("slide.home"),
        icon: <Icon id="r4-c5" />,
        activeIcon: <Icon id="r4-c4" />,
        to: "/home",
      },
      {
        title: t("slide.my_investment"),
        icon: <Icon id="r4-c9" />,
        activeIcon: <Icon id="r4-c8" />,
        to: "/my-investment",
      },
      {
        title: t("slide.my_wallet"),
        icon: <Icon id="r6-c9" />,
        activeIcon: <Icon id="r6-c8" />,
        to: "/my-wallet",
      },
      {
        title: t("slide.more"),
        icon: <Icon id="r6-c5" />,
        activeIcon: <Icon id="r6-c4" />,
        to: "/my-account",
      },
    ],
    [lang]
  );

  const SidebarComponent = largeScreen ? Drawer : Fragment;
  return (
    <SidebarComponent
      key="slide"
      {...(largeScreen
        ? {
            open,
            PaperProps: {
              elevation: 0,
              style: { backgroundColor: "transparent" },
            },
            onClose: () => dispatch({ openDrawer: false }),
            sx: {
              "& .MuiDrawer-paper": {
                right: lang === "ar" ? "0" : "auto",
                left: lang === "en" ? "0" : "auto",
              },
            },
          }
        : {})}
    >
      <div
        className={buildClass(
          "w-full h-screen text-[#fff]",
          "no-scrollbar overflow-y-scroll overflow-x-visible",
          "sticky top-0 ltr:left-0 rtl:right-0",
          checkClass(largeScreen, ["min-w-[280px]", "max-w-[250px]"])
        )}
        style={{
          ...(["xs", "sm", "md", "lg"].includes(screenSize)
            ? {
                background: "url(/images/background/back-login.webp)",
                backgroundImage: `url(/images/background/back-login.webp), 
              linear-gradient(275.01deg, #2B12F5 0.01%, #2B64F5 105.54%, #5CAEFF 136.9%)`,
              }
            : {}),
        }}
      >
        <div className="w-full box-border max-w-[202px]">
          <div className="pb-[56px] pt-10 ltr:pl-8 rtl:pr-8">
            <Link href="/" className="block w-fit">
              <Icon
                id="LogoWhite"
                className="logo cursor-pointer"
                width="95px"
                height="38px"
                viewBox="0 0 173 64"
                onClick={() => dispatch({ openDrawer: false })}
              />
            </Link>
          </div>

          {!user ? (
            <UserSkeleton />
          ) : (
            <ProfileItem removeIndecator={!complete_kyc} />
          )}
        </div>
        <div className="sm:hidden py-3">
          <NavBar fromSlide />
        </div>

        <div
          className={buildClass(
            "relative flex flex-col justify-between gap-10 mt-10 sm:mt-0",
            checkClass(complete_kyc, [
              "sm:py-[56px] sm:h-[calc(100%-250px)]",
              "sm:py-[40px] sm:h-[calc(100%-281px)]",
            ])
          )}
        >
          <div className="flex flex-col items-start gap-10 ">
            {links.map((link) => (
              <SidebarLink key={link.title} link={link} dispatch={dispatch} />
            ))}
          </div>
          <div
            className={`flex flex-col gap-10 ${complete_kyc ? "mt-10" : ""}`}
          >
            <DownloadAppLink t={t} />
            <LogoutButton t={t} />
          </div>
        </div>
      </div>
    </SidebarComponent>
  );
};

export default Slide;
